import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { AdminListCard } from "@components/CatalogIndex";
import type { BatchAction } from "@components/CatalogIndex";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useV2DeleteProjectLocationMutation } from "@generated";
import useListLocations from "../api/useListLocations";
import ImportLocationsSlideout from "../ImportLocationsSlideout/ImportLocationsSlideout";
import { Location } from "../types/Location";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  projectStatus: string;
  permission: string;
}

const Show: React.FC<Props> = ({ projectId, projectStatus, permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [importSlideoutOpen, setImportSlideoutOpen] = useState(false);

  const [deleteLocation] = useV2DeleteProjectLocationMutation();

  const { locations, locationsLoading, locationsFetching, pageMeta, refetch } =
    useListLocations({
      projectId,
      page: { number: pageNumber, size: pageSize },
    });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Location updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Location created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const handleDelete = () => {
    setDeleteQueue([...selectedIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    const promises = deleteQueue.map((id) =>
      deleteLocation({ id }).unwrap()
    );

    Promise.all(promises)
      .then(() => {
        setSuccessMessage(
          `Location${deleteQueue.length > 1 ? "s" : ""} deleted successfully!`
        );
        setShowSuccessAlert(true);
        setIsDeleting(false);
        setShowConfirmationModal(false);
        setSelectedIds([]);
        setDeleteQueue([]);
        refetch();
      })
      .catch(() => {
        setErrorMessages(
          "One or more locations could not be deleted. Please try again later."
        );
        setShowFailAlert(true);
        setIsDeleting(false);
      });
  };

  const getConfirmationMessage = () => (
    <div style={{ color: "#1C2655", fontSize: "14px" }}>
      {isDeleting ? (
        <SmallSpinner />
      ) : (
        <span style={{ fontWeight: "700" }}>
          {`You are about to delete ${selectedIds.length} location${
            selectedIds.length > 1 ? "s" : ""
          }.`}
        </span>
      )}
      <ul style={{ marginTop: "10px" }}>
        {selectedIds.map((id) => {
          const locName = locations.find((l) => l.id === id)?.name;
          return <li key={id}>{locName}</li>;
        })}
      </ul>
      This action cannot be undone. Do you wish to proceed?
    </div>
  );

  const formatAddress = (loc: Location): string => {
    const parts = [
      loc.street,
      loc.street2,
      loc.city,
      loc.state,
      loc.postalCode,
      loc.country ? loc.country.toUpperCase() : "",
    ].filter(Boolean);
    return parts.join(", ");
  };

  const columns: ColumnDef<Location>[] = [
    {
      header: "Location",
      cell(props) {
        const loc = props.row.original;
        if (loc.id === "0") {
          return (
            <div
              className={styles.editLink}
              onClick={() => {
                navigate(`/projects/${projectId}/project_locations/new`, {
                  state: {},
                });
              }}
            >
              {loc.name}
            </div>
          );
        }
        return (
          <div
            className={styles.editLink}
            onClick={() => {
              navigate(
                `/projects/${projectId}/project_locations/edit/${loc.id}`,
                { state: { location: loc } }
              );
            }}
          >
            {loc.name} <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Address",
      cell(props) {
        return (
          <span className={styles.addressCell}>
            {formatAddress(props.row.original)}
          </span>
        );
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className={styles.page}>
        You do not have permission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  const canManage = projectStatus === "building";

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: handleDelete,
      variant: "danger",
    },
  ];

  return (
    <div className={styles.page}>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminListCard
        title="Project Locations"
        onNew={
          canManage
            ? () =>
                navigate(`/projects/${projectId}/project_locations/new`, {
                  state: {},
                })
            : undefined
        }
        newButtonLabel="Add Location"
        secondaryAction={
          canManage
            ? {
                label: "Import Locations",
                onClick: () => setImportSlideoutOpen(true),
              }
            : undefined
        }
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        {locationsLoading || locationsFetching ? (
          <ScopeStackSpinner />
        ) : (
          <DataTable
            data={
              locations.length > 0
                ? locations
                : [
                    {
                      id: "0",
                      name: "Add locations to get started",
                      street: "",
                      street2: "",
                      city: "",
                      state: "",
                      postalCode: "",
                      country: "",
                      remote: false,
                      projectVariables: [],
                    },
                  ]
            }
            columns={columns}
            selectable={
              locations.length > 0 && permission !== "view" && canManage
            }
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            paginationEnabled
            pageSizeEnabled
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
        <ScopeStackModal
          modalTitle="Delete Location"
          modalBody={getConfirmationMessage()}
          handleButton2Click={isDeleting ? () => null : confirmDelete}
          handleClose={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          show={showConfirmationModal}
          handleButton1Click={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          deleteModal={true}
          button1Text="Cancel"
          button2Text="Delete"
        />
      </AdminListCard>
      <ImportLocationsSlideout
        projectId={projectId}
        isOpen={importSlideoutOpen}
        onClose={() => setImportSlideoutOpen(false)}
      />
    </div>
  );
};

export default Show;
